import {
  MOBILE,
  PHABLET,
  TABLET,
  DESKTOP_SM,
  DESKTOP_MD,
  DESKTOP_LG
} from "../constants/screenSize";

export const isElementInView = (node, offset, mode) => {
  offset = offset || 0;
  mode = mode || "visible";

  const rect = node.getBoundingClientRect();
  const viewHeight = Math.max(
    document.documentElement.clientHeight,
    window.innerHeight
  );
  const above = rect.bottom - offset < 0;
  const below = rect.top - viewHeight + offset >= 0;

  return mode === "above" ? above : mode === "below" ? below : !above && !below;
};

/**
 * Checks if the entire element is in View-Port
 * @param {dom element} node
 * @param {boundries} offset
 */
export const isElementInViewPort = (node, offset = 0) => {
  let visible = false;
  if (node) {
    const viewPort = window.innerHeight;
    const rect = node.getBoundingClientRect();
    visible = rect.bottom <= viewPort - offset && rect.top >= 0;
  }
  return visible;
};

/**
 * Checks if a dom element is phisically visible in the DOM.
 * (Not only inside the View-Port)
 * @param {DOM Element} node
 */
export const isElementVisibleOnDOM = node =>
  node && node.offsetHeight > 0 && node.offsetWidth > 0;

export const extendedLocalStorage = {
  /**
   * @param {string} key string
   * @param {object} jsonData
   * @param {number} expirationMin in minutes
   */
  setItem: function(key, jsonData, expirationMin) {
    if (typeof Storage !== "function") {
      return false;
    }
    let expirationMS = expirationMin * 60 * 1000;
    let record = {
      value: JSON.stringify(jsonData),
      timestamp: new Date().getTime() + expirationMS
    };
    localStorage.setItem(key, JSON.stringify(record));
    return jsonData;
  },
  /**
   * @param {string} key key to get value from
   */
  getItem: function(key) {
    if (typeof Storage !== "function") {
      return false;
    }
    let record = JSON.parse(localStorage.getItem(key));
    if (!record) {
      return false;
    }
    return new Date().getTime() < record.timestamp && JSON.parse(record.value);
  }
};

export const extendedSessionStorage = {
  /**
   * @param {string} key string
   * @param {object} jsonData
   */
  setItem: function(key, jsonData) {
    if (typeof Storage !== "function") {
      return false;
    }
    let record = {
      value: JSON.stringify(jsonData)
    };
    sessionStorage.setItem(key, JSON.stringify(record));
    return jsonData;
  },
  /**
   * @param {string} key key to get value from
   */
  getItem: function(key) {
    if (typeof Storage !== "function") {
      return false;
    }
    let record = JSON.parse(sessionStorage.getItem(key));
    if (!record) {
      return false;
    }
    return JSON.parse(record.value);
  }
};

export const getCurrentScreenType = () => {
  const screenSize = window.innerWidth;
  const screenTypes = [
    MOBILE,
    PHABLET,
    TABLET,
    DESKTOP_SM,
    DESKTOP_MD,
    DESKTOP_LG
  ];

  const current = screenTypes.find(st => screenSize <= st);
  return current || MOBILE;
};

export const toggleBodyScrolling = allowScroll => {
  document.body.style.position = allowScroll ? "initial" : "relative";
  document.body.style.overflow = allowScroll ? "initial" : "hidden";
};

export function requestFullScreen() {
  let supportedFunc;
  let supportedElement;
  if (document.requestFullScreen) {
    // standard on document directly
    supportedFunc = document.requestFullScreen;
    supportedElement = document;
  } else if (document.mozRequestFullScreen) {
    // mozila firefox on document directly
    supportedFunc = document.mozRequestFullScreen;
    supportedElement = document;
  } else if (document.webkitRequestFullScreen) {
    //  webkit (chrome) on document directly
    supportedFunc = document.webkitRequestFullScreen;
    supportedElement = document;
  } else if (document.msRequestFullscreen) {
    // microsoft on document directly
    supportedFunc = document.msRequestFullscreen;
    supportedElement = document;
  } else if (document.documentElement.requestFullScreen) {
    // standard on documentElement
    supportedFunc = document.documentElement.requestFullScreen;
    supportedElement = document.documentElement;
  } else if (document.documentElement.mozRequestFullScreen) {
    //  mozila firefox on documentElement
    supportedFunc = document.documentElement.mozRequestFullScreen;
    supportedElement = document.documentElement;
  } else if (document.documentElement.webkitRequestFullScreen) {
    // webkit (chrome) on documentElement
    supportedFunc = document.documentElement.webkitRequestFullScreen;
    supportedElement = document.documentElement;
  } else if (document.documentElement.msRequestFullscreen) {
    // microsoft on documentElement
    supportedFunc = document.documentElement.msRequestFullscreen;
    supportedElement = document.documentElement;
  }

  return supportedFunc.bind(supportedElement)();
}

export function exitFullScreen() {
  let supportedFunc;
  let supportedElement;
  if (document.exitFullscreen) {
    // standard on document directly
    supportedFunc = document.exitFullscreen;
    supportedElement = document;
  } else if (document.mozCancelFullScreen) {
    // mozila firefox on document directly
    supportedFunc = document.mozCancelFullScreen;
    supportedElement = document;
  } else if (document.webkitExitFullscreen) {
    //  webkit (chrome) on document directly
    supportedFunc = document.webkitExitFullscreen;
    supportedElement = document;
  } else if (document.msExitFullscreen) {
    // microsoft on document directly
    supportedFunc = document.msExitFullscreen;
    supportedElement = document;
  } else if (document.documentElement.exitFullscreen) {
    // standard on documentElement
    supportedFunc = document.documentElement.exitFullscreen;
    supportedElement = document.documentElement;
  } else if (document.documentElement.mozCancelFullScreen) {
    //  mozila firefox on documentElement
    supportedFunc = document.documentElement.mozCancelFullScreen;
    supportedElement = document.documentElement;
  } else if (document.documentElement.webkitExitFullscreen) {
    // webkit (chrome) on documentElement
    supportedFunc = document.documentElement.webkitExitFullscreen;
    supportedElement = document.documentElement;
  } else if (document.documentElement.msExitFullscreen) {
    // microsoft on documentElement
    supportedFunc = document.documentElement.msExitFullscreen;
    supportedElement = document.documentElement;
  }
  if (supportedFunc && supportedElement) {
    supportedFunc.bind(supportedElement)();
  }
}

export function toggleRequestFullScreen(doFullScreen) {
  /* We will return a promise to inline with the standard spec
  * Though most of the browsers not returning a promise.
  * We will reject on catch if the browser doesn't support RequestFullScreen
  */

  const promise = new Promise((resolve, reject) => {
    try {
      if (doFullScreen) {
        requestFullScreen && requestFullScreen();
      } else {
        exitFullScreen && exitFullScreen();
      }
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          resolve("no errors, fullscreen may worked");
        });
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn("toggleRequestFullScreen failed - ", error);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          reject(error);
        });
      });
    }
  });

  return promise;
}

/** creates a dummy element, setting inner html content and grabing it back as text */
export function removeHTMLFromString(input) {
  const dummyElement = document.createElement("div");
  dummyElement.innerHTML = input;
  const safeInput = dummyElement.textContent || dummyElement.innerText;
  return safeInput;
}

export const storagedSearchDataManager = () => {
  let stack = extendedSessionStorage.getItem("serverSearchData") || [];
  const setStackToSessionStorage = () =>
    extendedSessionStorage.setItem("serverSearchData", stack, 120);

  // always keep first element as null for edge cases like (one search && checkout submit)

  const popFromStack = () => {
    if (stack.length < 2) {
      return;
    }
    stack.pop();
    let result = stack.pop();
    stack.push(null);
    setStackToSessionStorage();
    return result;
  };
  const pushToStack = sd => {
    if (stack.length > 0) {
      stack.pop();
    }
    stack.push(sd);
    stack.push(null);
    setStackToSessionStorage();
  };

  return {
    getLastSearchData: popFromStack,
    addNewSearchData: pushToStack
  };
};



// WEBPACK FOOTER //
// ./src/helpers/domHelpers.js